// microneedlingData.ts

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string;
        type?: string;
        title?: string;
        description?: string;
        url?: string;
        site_name?: string;
        updated_time?: string;
        image?: string;
        image_secure_url?: string;
        image_width?: number;
        image_height?: number;
        image_alt?: string;
        image_type?: string;
        video?: string;
        video_duration?: number;
        video_upload_date?: string;
        video_allow_embed?: boolean;
    };
    twitter?: {
        card?: string;
        title?: string;
        description?: string;
        image?: string;
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    treatmentAreas1: TreatmentArea[];
    treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string;
        beforeAfter: string[];
        introductionImage?: string;
    };
    product: {
        id: number;
        title: string;
        price: number;
        description: string;
    };
}

export const microneedling: {
    en: LanguageContent;
    ar: LanguageContent;
    shared: SharedData;
} =
{
    "en": {
        "url": "",
        "meta": {
            "title": "Microneedling Treatment in Dubai -",
            "description": "Microneedling is a minimally invasive cosmetic procedure designed to rejuvenate the skin by stimulating collagen production. This treatment, also known as",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/services/microneedling-treatment-dubai/",
            "og": {
                "locale": "en_US",
                "type": "article",
                "title": "Microneedling Treatment in Dubai -",
                "description": "Microneedling is a minimally invasive cosmetic procedure designed to rejuvenate the skin by stimulating collagen production. This treatment, also known as",
                "url": "https://elitebodyhome.com/services/microneedling-treatment-dubai/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2024-12-30T09:18:17+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-34.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-34.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "Microneedling Treatment in Dubai",
                "image_type": "image/jpeg"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "Microneedling Treatment in Dubai -",
                "description": "Microneedling is a minimally invasive cosmetic procedure designed to rejuvenate the skin by stimulating collagen production. This treatment, also known as",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-34.jpg"
            },
        },
        "breadcrumbs": ["Home", "Services", "Microneedling Treatment in Dubai"],
        "hero": {
            "title": "Microneedling Treatment in Dubai",
            "subtitle": "Transform Your Skin with the Power of Microneedling",
            "description": "Microneedling is a minimally invasive cosmetic treatment that helps improve skin texture and tightness by creating micro-injuries to promote collagen production. Also called collagen induction therapy, this treatment uses tiny needles to create microscopic holes in the skin. These micro-injuries to the skin, however controlled, activate the body's inherent healing system, resulting in a smoother, rejuvenated and firmer appearance."
        },
        "howItWorks": {
            "title": "",
            "description": "Microneedling is now a popular treatment for most skin conditions as it can treat everything from fine lines and wrinkles to acne scars and large pores. A non-surgical and minimal-downtime option suitable for men and women alike, it is a controlled and predictable way to improve not just skin texture and tone, but also the skin itself."
        },
        "procedureSteps": {
            "heading": "Procedure",
            "para": "Microneedling is a process that uses a specialized device with fine needles to create controlled microchannels in the skin. It is typically well tolerated, but a numbing cream is placed ahead of time to make it more comfortable.",
            "steps": [
                {
                    "title": "Preparation",
                    "desc": "Your skin will be cleaned, and a numbing agent is used to reduce discomfort.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "Needling Process",
                    "desc": "A device with many fine needles is skimmed over the skin. These needles enter the top layer of skin, making little punctures.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "Healing",
                    "desc": "These micro-injuries encourage the skin to generate more collagen and elastin, proteins critical to youthful, healthy skin.",
                    "icon": "fa-solid fa-heartbeat"
                },
                {
                    "title": "Post-Treatment Care",
                    "desc": "A calming serum or cream is applied to help heal and reduce redness or irritation.",
                    "icon": "fa-solid fa-band-aid"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits",
            "subheading": "",
            "para": "Microneedling provides a host of skin-illuminating benefits that enhance appearance and texture. Because the treatment can trigger collagen, it's a perfect way to tackle several skin problems at once.",
            "items": [
                {
                    "title": "Lowers the appearance of fine lines and wrinkles",
                    "desc": "Microneedling also promotes collagen production, which helps reduce fine lines and deep wrinkles.",
                    "icon": "fa-solid fa-arrows-alt-v"
                },
                {
                    "title": "Improves skin texture",
                    "desc": "Microneedling helps to diminish the look of rough skin to give you a more uniform texture.",
                    "icon": "fa-solid fa-texture"
                },
                {
                    "title": "Reduces acne scars",
                    "desc": "The treatment works especially well in minimizing the appearance of acne scars, resulting in smooth skin.",
                    "icon": "fa-solid fa-circle"
                },
                {
                    "title": "Enhances product absorption",
                    "desc": "Finally, the microscopic channels made in the skin allow for better absorption of skincare products applied post-treatment, maximizing results.",
                    "icon": "fa-solid fa-tint"
                },
                {
                    "title": "Minimizes large pores",
                    "desc": "Microneedling can also shrink large pores to make them look more refined.",
                    "icon": "fa-solid fa-dot-circle"
                },
                {
                    "title": "Treats hyperpigmentation",
                    "desc": "It also can diminish dark pigmentation areas like age spots or sun damage.",
                    "icon": "fa-solid fa-adjust"
                }
            ]
        },
        "eligibility": {
            "heading": "Eligibility",
            "subheading": "",
            "para": "Microneedling is an effective treatment for many skin types and concerns, but there are some conditions that may make it less suitable for certain patients.",
            "items": [
                {
                    "title": "Anyone wanting to minimize the appearance of wrinkles, fine lines, or acne scars.",
                    "desc": "",
                    "icon": "fa-solid fa-check-circle"
                },
                {
                    "title": "Those who are looking to improve skin texture or tone.",
                    "desc": "",
                    "icon": "fa-solid fa-check-circle"
                },
                {
                    "title": "The best candidates are those with enlarged pores or minor pigmentation issues.",
                    "desc": "",
                    "icon": "fa-solid fa-check-circle"
                },
                {
                    "title": "People who have healthy skin without active infections or acne flares.",
                    "desc": "",
                    "icon": "fa-solid fa-check-circle"
                }
            ]
        },
        "preparationTips": {
            "heading": "Preparation",
            "subheading": "",
            "para": "The guide is an important step that every microneedling treatment should go through to help you prepare yourself accordingly. (If you follow pre-treatment instructions, you have a better chance of minimizing side effects and maximizing healing.)",
            "items": [
                {
                    "title": "Avoid direct sun exposure",
                    "desc": "Avoid sunbathing or tanning beds, at minimum, a week prior to treatment to avoid irritation.",
                    "icon": "fa-solid fa-sun"
                },
                {
                    "title": "Discontinue retinoids",
                    "desc": "Refrain from using retinoid products and other aggressive skin care ingredients such as glycolic acid or salicylic acid for 3–5 days before treatment.",
                    "icon": "fa-solid fa-ban"
                },
                {
                    "title": "Stay hydrated",
                    "desc": "Make sure to stay hydrated by drinking enough water.",
                    "icon": "fa-solid fa-tint"
                },
                {
                    "title": "Avoid blood-thinning agents",
                    "desc": "Also, if you can help it, stay off medications like aspirin or ibuprofen for a few days before the procedure, as these can make you more prone to bruising.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "Consultation",
                    "desc": "Consult with your provider about any medications, allergies or skin sensitivities you have.",
                    "icon": "fa-solid fa-user-md"
                }
            ]
        },
        "results": {
            "heading": "Results",
            "subheading": "",
            "para": "Microneedling has gradual and long lasting results making it popular for a non-surgical plans of action of skin restoring. The biggest skin changes will typically be noted 4-6 weeks after your 1st session, with ongoing improvements as your collagen builds.",
            "items": [
                {
                    "title": "Smoother, firmer skin",
                    "desc": "Skin also grows more elastic and firm, forming new collagen.",
                    "icon": "fa-solid fa-leaf"
                },
                {
                    "title": "Gotten rid of the appearance of wrinkles and scars",
                    "desc": "Gradually, fine lines, wrinkles, and scars fade away, resulting in younger-looking skin.",
                    "icon": "fa-solid fa-magic"
                },
                {
                    "title": "Improved skin tone",
                    "desc": "Skin appears more even, and areas of hyperpigmentation diminish with regular treatments.",
                    "icon": "fa-solid fa-palette"
                },
                {
                    "title": "Long-lasting effects",
                    "desc": "Results can last for up to a year with proper skin care and maintenance treatments once the collagen has rebuilt.",
                    "icon": "fa-solid fa-clock"
                }
            ]
        },
        "aftercareTips": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    "question": "Does microneedling hurt?",
                    "answer": "Most patients describe the sensation as mild discomfort or tingling. A topical numbing cream is used to minimize any pain."
                },
                {
                    "question": "How long is the recovery time?",
                    "answer": "Mild redness and swelling may occur for 1-3 days post-treatment. Full recovery typically takes about a week."
                },
                {
                    "question": "When will I see results?",
                    "answer": "Initial improvements in skin texture and tone can be seen within a week. More noticeable results, such as reduced wrinkles or scars, appear over 4-6 weeks as collagen production increases."
                },
                {
                    "question": "How many treatments are needed?",
                    "answer": "Most patients require 3-6 treatments spaced about 4-6 weeks apart for optimal results."
                },
                {
                    "question": "Can I wear makeup after microneedling?",
                    "answer": "It's best to avoid makeup for at least 24 hours post-treatment to allow the skin to heal."
                },
                {
                    "question": "Are there any side effects?",
                    "answer": "Common side effects include temporary redness, swelling, or mild irritation. These usually subside within a few days."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    },
    "shared": {
        "images": {
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-34.jpg",
            "beforeAfter": [
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/karismabefore-and-after-in-ebh-1.jpg",
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/karismabefore-and-after-in-ebh-1.jpg",
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/PRP-Facial-Treatment-Dubaid-2.jpg"
            ]
        },
        "product": {
            "id": 109,
            "title": "Microneedling Treatment",
            "price": 499,
            "description": "Microneedling is a minimally invasive cosmetic procedure designed to rejuvenate the skin by stimulating collagen production."
        }
    },

    "ar": {
        "url": "",
        "meta": {
            "title": "علاج الوخز بالإبر الدقيقة في دبي - تجديد البشرة وتحفيز الكولاجين",
            "description": "علاج الوخز بالإبر الدقيقة (الميكرونيدلينج) في دبي هو إجراء تجميلي طفيف التوغل يحفز إنتاج الكولاجين لتحسين مظهر التجاعيد، الندبات، وتصبغات البشرة. احصل على بشرة أكثر شبابًا في ايليت بودي هوم.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/services/microneedling-treatment-dubai/",
            "og": {
                "locale": "ar_AR",
                "type": "article",
                "title": "علاج الوخز بالإبر الدقيقة في دبي - تجديد البشرة وتحفيز الكولاجين",
                "description": "علاج الوخز بالإبر الدقيقة (الميكرونيدلينج) في دبي هو إجراء تجميلي طفيف التوغل يحفز إنتاج الكولاجين لتحسين مظهر التجاعيد، الندبات، وتصبغات البشرة. احصل على بشرة أكثر شبابًا في ايليت بودي هوم.",
                "url": "https://elitebodyhome.com/services/microneedling-treatment-dubai/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2024-12-30T09:18:17+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-34.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-34.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "علاج الوخز بالإبر الدقيقة في دبي",
                "image_type": "image/jpeg"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "علاج الوخز بالإبر الدقيقة في دبي - تجديد البشرة وتحفيز الكولاجين",
                "description": "علاج الوخز بالإبر الدقيقة (الميكرونيدلينج) في دبي هو إجراء تجميلي طفيف التوغل يحفز إنتاج الكولاجين لتحسين مظهر التجاعيد، الندبات، وتصبغات البشرة. احصل على بشرة أكثر شبابًا في ايليت بودي هوم.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-34.jpg"
            }
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "علاج الوخز بالإبر الدقيقة في دبي"],
        "hero": {
            "title": "علاج الوخز بالإبر الدقيقة في دبي",
            "subtitle": "حولي بشرتك بقوة الوخز بالإبر الدقيقة",
            "description": "الوخز بالإبر الدقيقة (الميكرونيديلنغ) هو إجراء تجميلي طفيف التوغل، يُساهم في تحسين ملمس البشرة وشدها من خلال إحداث ثقوب دقيقة لتحفيز إنتاج الكولاجين. ويُعرف أيضًا باسم علاج تحفيز الكولاجين، حيث يُستخدم جهاز مزود بإبر دقيقة لإنشاء ثقوب مجهرية في الجلد. ورغم أن هذه الثقوب مجهرية ومُتحكم بها، إلا إنها تُنشط عملية الشفاء الطبيعية في الجسم، مما يؤدي إلى بشرة أكثر نعومة وتماسكًا."
        },
        "howItWorks": {
            "title": "",
            "description": "قد أصبح الميكرونيديلنغ علاجًا شائعًا لمجموعة واسعة من مشكلات البشرة، إذ يساعد في تحسين مظهر الخطوط الرفيعة، والتجاعيد، وندبات حب الشباب، والمسام الواسعة. حيث يُعد خيار غير جراحي ولا يحتاج إلى فترة تعافٍ طويلة، ويناسب الرجال والنساء على حد سواء، ويُعتبر وسيلة دقيقة وآمنة لتحسين نسيج البشرة، وتوحيد لونها، وتعزيز نضارتها من الداخل إلى الخارج."
        },
        "procedureSteps": {
            "heading": "خطوات إجراء الوخز بالإبر الدقيقة",
            "para": "يُستخدم جهاز خاص يحتوي على إبر دقيقة لإنشاء ثقوب مجهرية دقيقة مُتحكم بها في الجلد. يكون العلاج محتملًا عادةً، ولكن يمكن استخدام كريم مخدر مسبقًا لتقليل أي شعور بالإزعاج أثناء الجلسة.",
            "steps": [
                {
                    "title": "الاستعداد",
                    "desc": "تُنظف البشرة جيدًا أولًا، ثم يُستخدم كريم مخدر لتقليل الشعور بالألم أثناء الجلسة.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "مرحلة الوخز",
                    "desc": "يُمرر جهاز يحتوي على عدد كبير من الإبر الدقيقة بلطف على سطح الجلد. تخترق هذه الإبر الطبقة العليا من البشرة، مُحدثة ثقوبًا صغيرة جدًا.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "التعافي",
                    "desc": "تُحفز هذه الثقوب المجهرية البشرة على إنتاج المزيد من الكولاجين والإيلاستين، وهما من البروتينات الأساسية للحفاظ على شباب البشرة وصحتها.",
                    "icon": "fa-solid fa-heartbeat"
                },
                {
                    "title": "العناية بعد العلاج",
                    "desc": "يُستخدم سيروم مهدئ أو كريم للمساعدة في التئام الجلد والتقليل من الاحمرار أو التهيج الناتج عن الجلسة.",
                    "icon": "fa-solid fa-band-aid"
                }
            ]
        },
        "benefits": {
            "heading": "الفوائد",
            "subheading": "",
            "para": "يوفر الوخز بالإبر الدقيقة مجموعة من الفوائد التي تُحسن مظهر البشرة وملمسها بشكل واضح، حيث يُعد خيارًا مثاليًا لمعالجة عدة مشكلات جلدية في آنٍ واحد، نظرًا لدوره في تحفيز إنتاج الكولاجين.",
            "items": [
                {
                    "title": "يخفف من مظهر الخطوط الدقيقة والتجاعيد",
                    "desc": "يساهم في تحفيز إنتاج الكولاجين، مما يساعد على تقليل التجاعيد الواضحة والخطوط الرفيعة.",
                    "icon": "fa-solid fa-arrows-alt-v"
                },
                {
                    "title": "تحسين ملمس البشرة",
                    "desc": "يساعد في تنعيم مظهر البشرة الخشنة ويمنحها ملمسًا موحدًا.",
                    "icon": "fa-solid fa-texture"
                },
                {
                    "title": "تخفيف ندبات حب الشباب",
                    "desc": "يعد علاج فعال جدًا في تقليل مظهر ندبات حب الشباب، مما يُعيد للبشرة مظهرًا أكثر نعومة.",
                    "icon": "fa-solid fa-circle"
                },
                {
                    "title": "تعزيز امتصاص البشرة للمنتجات",
                    "desc": "تسمح الثقوب الدقيقة التي يُحدثها في الجلد بامتصاص أفضل للمنتجات المستخدمة بعد الجلسة، لتحقيق أقصى نتائج ممكنة.",
                    "icon": "fa-solid fa-tint"
                },
                {
                    "title": "تصغير المسام الواسعة",
                    "desc": "يساهم في تقليص حجم المسام الواسعة ويمنح البشرة مظهرًا أكثر صفاءً.",
                    "icon": "fa-solid fa-dot-circle"
                },
                {
                    "title": "علاج فرط التصبغ",
                    "desc": "يساعد على تخفيف البقع الداكنة مثل التصبغات الناتجة عن التقدم في العمر أو أضرار الشمس.",
                    "icon": "fa-solid fa-adjust"
                }
            ]
        },
        "eligibility": {
            "heading": "الأهلية",
            "subheading": "",
            "para": "يُعد الميكرونيديلنغ علاجًا فعالًا للعديد من أنواع البشرة والحالات الجلدية، إلا أن هناك بعض العوامل التي قد تجعله غير مناسبًا لبعض الأشخاص.",
            "items": [
                {
                    "title": "الأشخاص الذين يرغبون في تقليل مظهر التجاعيد أو الخطوط الدقيقة أو ندبات حب الشباب.",
                    "desc": "",
                    "icon": "fa-solid fa-check-circle"
                },
                {
                    "title": "الأشخاص الذين يسعون لتحسين ملمس البشرة وتوحيد لونها.",
                    "desc": "",
                    "icon": "fa-solid fa-check-circle"
                },
                {
                    "title": "الأشخاص الذين يعانون من المسام الواسعة أو التصبغات البسيطة.",
                    "desc": "",
                    "icon": "fa-solid fa-check-circle"
                },
                {
                    "title": "الأشخاص الذين يتمتعون ببشرة صحية خالية من الالتهابات أو حالات حب الشباب النشطة.",
                    "desc": "",
                    "icon": "fa-solid fa-check-circle"
                }
            ]
        },
        "preparationTips": {
            "heading": "الاستعداد",
            "subheading": "",
            "para": "يُعد اتباع هذه الإرشادات خطوة هامة قبل كل جلسة، لمساعدتك على الاستعداد بشكل مناسب. حيث يساهم التزامك بهذا الإرشادات في تقليل الآثار الجانبية وتعزيز عملية التعافي بشكل أفضل.",
            "items": [
                {
                    "title": "تجنب التعرض المباشر لأشعة الشمس",
                    "desc": "تجنب التعرض للشمس أو أجهزة التسمير لمدة لا تقل عن أسبوع قبل الجلسة لتفادي تهيج البشرة.",
                    "icon": "fa-solid fa-sun"
                },
                {
                    "title": "التوقف عن استخدام الريتينويدات",
                    "desc": "امتنع عن استخدام منتجات الريتينويد أو أي مستحضرات تحتوي على مواد فعالة قوية مثل حمض الجليكوليك أو الساليسيليك لمدة 3 إلى 5 أيام قبل الجلسة.",
                    "icon": "fa-solid fa-ban"
                },
                {
                    "title": "الحفاظ على الترطيب",
                    "desc": "احرص على شرب كميات كافية من الماء قبل العلاج.",
                    "icon": "fa-solid fa-tint"
                },
                {
                    "title": "تجنب مُميعات الدم",
                    "desc": "توقف - إن أمكن - عن تناول الأدوية المُميعة للدم مثل الأسبرين أو الإيبوبروفين لبضعة أيام قبل الجلسة لتقليل خطر حدوث الكدمات.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "استشارة الطبيب",
                    "desc": "استشر الطبيب قبل الجلسة بشأن أي أدوية تتناولها أو أي حساسية أو تحسّس جلدي تعاني منه.",
                    "icon": "fa-solid fa-user-md"
                }
            ]
        },
        "results": {
            "heading": "النتائج",
            "subheading": "",
            "para": "يوفر علاج الوخز بالإبر الدقيقة نتائج تدريجية وطويلة الأمد، مما يجعله من الخيارات الشائعة غير الجراحية في خطط تجديد البشرة. تبدأ التغييرات الملحوظة بالظهور عادةً بعد 4 إلى 6 أسابيع من الجلسة الأولى، وتستمر التحسينات تدريجياً مع تحفيز إنتاج الكولاجين في البشرة.",
            "items": [
                {
                    "title": "بشرة أكثر نعومة وتماسكاً",
                    "desc": "تصبح البشرة أكثر مرونة ومتانة مع الوقت نتيجة إنتاج الكولاجين الجديد.",
                    "icon": "fa-solid fa-leaf"
                },
                {
                    "title": "تلاشي مظهر التجاعيد والندبات",
                    "desc": "تتضاءل ملامح الخطوط الدقيقة والتجاعيد والندبات تدريجياً، ما يمنح البشرة مظهراً أكثر شباباً.",
                    "icon": "fa-solid fa-magic"
                },
                {
                    "title": "توحيد لون البشرة",
                    "desc": "يتوحد لون البشرة، وتقل التصبغات مع الجلسات المنتظمة.",
                    "icon": "fa-solid fa-palette"
                },
                {
                    "title": "نتائج تدوم طويلًا",
                    "desc": "يمكن أن تدوم النتائج حتى عام كامل مع العناية المناسبة بالبشرة وجلسات المتابعة عند الحاجة بعد اكتمال إعادة بناء الكولاجين في البشرة.",
                    "icon": "fa-solid fa-clock"
                }
            ]
        },
        "aftercareTips": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "faqData": {
            "heading": "الأسئلة الشائعة",
            "subheading": "",
            "items": [
                {
                    "question": "هل يسبب الوخز بالإبر الدقيقة ألماً؟",
                    "answer": "يصف معظم المرضى الإحساس بعدم ارتياح خفيف أو وخز. يتم استخدام كريم مخدر موضعي لتقليل أي ألم."
                },
                {
                    "question": "كم تستغرق فترة التعافي؟",
                    "answer": "قد يحدث احمرار وتورم خفيف لمدة 1-3 أيام بعد العلاج. التعافي الكامل يستغرق عادة حوالي أسبوع."
                },
                {
                    "question": "متى سأرى النتائج؟",
                    "answer": "يمكن رؤية التحسينات الأولية في ملمس البشرة ولونها خلال أسبوع. تظهر نتائج أكثر وضوحاً، مثل تقليل التجاعيد أو الندبات، خلال 4-6 أسابيع مع زيادة إنتاج الكولاجين."
                },
                {
                    "question": "كم عدد الجلسات المطلوبة؟",
                    "answer": "معظم المرضى يحتاجون إلى 3-6 جلسات تفصل بينها حوالي 4-6 أسابيع للحصول على أفضل النتائج."
                },
                {
                    "question": "هل يمكنني وضع المكياج بعد الوخز بالإبر الدقيقة؟",
                    "answer": "من الأفضل تجنب المكياج لمدة 24 ساعة على الأقل بعد العلاج للسماح للبشرة بالشفاء."
                },
                {
                    "question": "هل هناك أي آثار جانبية؟",
                    "answer": "الآثار الجانبية الشائعة تشمل احمراراً مؤقتاً، تورماً، أو تهيجاً خفيفاً. عادة ما تختفي هذه الآثار خلال بضعة أيام."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    }

};
